/**
 * 인증 Context
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AdminUser, AuthState } from '../types/auth';
import * as authService from '../services/authService';

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    accessToken: null,
    isAuthenticated: false,
    isLoading: true,
  });

  // 초기 로드 시 저장된 인증 정보 확인
  useEffect(() => {
    const initAuth = async () => {
      try {
        const savedAdmin = authService.getAdminInfo();
        const token = authService.getToken();

        if (savedAdmin && token) {
          // 토큰이 있으면 서버에서 최신 정보 조회
          try {
            const response = await authService.getCurrentAdmin();
            if (response.success) {
              // API 응답이 { admin: {...} } 형태이므로 response.data.admin 사용
              const adminData = response.data.admin;
              setState({
                user: adminData,
                accessToken: token,
                isAuthenticated: true,
                isLoading: false,
              });
              authService.saveAdminInfo(adminData);
            } else {
              // 토큰이 유효하지 않으면 삭제
              authService.removeToken();
              authService.removeAdminInfo();
              setState({
                user: null,
                accessToken: null,
                isAuthenticated: false,
                isLoading: false,
              });
            }
          } catch (error) {
            // API 호출 실패 시 저장된 정보 삭제
            authService.removeToken();
            authService.removeAdminInfo();
            setState({
              user: null,
              accessToken: null,
              isAuthenticated: false,
              isLoading: false,
            });
          }
        } else {
          setState((prev) => ({ ...prev, isLoading: false }));
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    setState((prev) => ({ ...prev, isLoading: true }));
    try {
      const response = await authService.login({ email, password });
      // 로그인 후 최신 관리자 정보 조회 (role_group_name 포함)
      try {
        const currentAdminResponse = await authService.getCurrentAdmin();
        if (currentAdminResponse.success) {
          const adminData = currentAdminResponse.data.admin;
          setState({
            user: adminData,
            accessToken: response.access_token,
            isAuthenticated: true,
            isLoading: false,
          });
          authService.saveAdminInfo(adminData);
        } else {
          // getCurrentAdmin 실패 시 로그인 응답의 admin 정보 사용
          setState({
            user: response.admin,
            accessToken: response.access_token,
            isAuthenticated: true,
            isLoading: false,
          });
        }
      } catch (error) {
        // getCurrentAdmin 실패 시 로그인 응답의 admin 정보 사용
        console.error('Failed to get current admin info:', error);
        setState({
          user: response.admin,
          accessToken: response.access_token,
          isAuthenticated: true,
          isLoading: false,
        });
      }
    } catch (error: any) {
      setState((prev) => ({ ...prev, isLoading: false }));
      const errorMessage =
        error.response?.data?.error?.message || error.message || '로그인에 실패했습니다.';
      throw new Error(errorMessage);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setState({
        user: null,
        accessToken: null,
        isAuthenticated: false,
        isLoading: false,
      });
      authService.removeAdminInfo();
    }
  };

  const refreshUser = async (): Promise<void> => {
    try {
      const response = await authService.getCurrentAdmin();
      if (response.success) {
        const adminData = response.data.admin;
        setState((prev) => ({ ...prev, user: adminData }));
        authService.saveAdminInfo(adminData);
      }
    } catch (error) {
      console.error('Refresh admin info error:', error);
      // 에러 발생 시 로그아웃 처리
      await logout();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

